import { totalsByCurrency } from '../../platform/money/totals'
import type { LocalDate } from '../../platform/localDate'
import { monthBounds } from '../transactions/filters'
import type { DayBuckets } from './cashflowWire'

/**
 * Per-day and per-weekday spend for the Overview's "When you spend". The per-day
 * figures are the server's (backend #528); the per-weekday sum in `worstWeekday` is
 * the only client arithmetic left (the owner's exception, epic #3).
 */

export type DaySpend = { date: LocalDate; spentCents: number }
export type DailySpend = { month: string; currency: string; days: DaySpend[]; otherCurrencyCount: number }
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6

/** Mon = 0. UTC, so no local offset moves the weekday. */
export function weekdayOf(date: string): Weekday {
  const [y, m, d] = date.split('-').map(Number)
  return ((new Date(Date.UTC(y as number, (m as number) - 1, d as number)).getUTCDay() + 6) % 7) as Weekday
}

/**
 * The month laid out day by day from the server's day buckets (`/reports/cashflow?granularity=day`).
 * Each day's figure is the server's own `expense`; nothing is summed here. The heatmap asks for the whole
 * month, so the server returns a bucket for EVERY day up to the month end; future days are "0.00" with
 * `complete: false`, and "still to come" is decided later from the local date. If a window ended earlier,
 * the dates after `windowEnd` have no bucket and read 0. A date on or before `windowEnd` with no bucket is a
 * contract break and throws, as do a bucket outside the month and a repeated date.
 */
export function monthDays(buckets: DayBuckets, month: string, otherCurrencyCount: number): DailySpend {
  const { start, end } = monthBounds(month)
  const byDate = new Map(buckets.days.map((d) => [d.date, d.expenseCents]))
  if (byDate.size !== buckets.days.length) {
    const seen = new Set<string>()
    const again = buckets.days.find((d) => seen.size === seen.add(d.date).size)
    throw new Error(`monthDays: ${again?.date ?? 'a date'} appears twice`)
  }
  for (const d of buckets.days) {
    if (d.date < start || d.date > end) throw new Error(`monthDays: ${d.date} is outside ${month}`)
  }
  const last = Number(end.slice(8))
  const days: DaySpend[] = []
  for (let d = 1; d <= last; d++) {
    const date = `${month}-${String(d).padStart(2, '0')}` as LocalDate
    const cents = byDate.get(date)
    if (cents === undefined && date <= buckets.windowEnd) throw new Error(`monthDays: no bucket for ${date}`)
    days.push({ date, spentCents: cents ?? 0 })
  }
  return { month, currency: buckets.currency, days, otherCurrencyCount }
}

/**
 * The heaviest weekday by summed spend; null when nothing was spent or two weekdays tie for the top.
 * **The only client arithmetic left in this feature**: a per-weekday sum of the server's per-day figures,
 * inside the owner's exception (epic #3), through `totalsByCurrency` so currencies stay apart. It goes
 * away if the backend returns a weekday breakdown.
 */
export function worstWeekday(days: readonly DaySpend[], currency: string): Weekday | null {
  const per = new Map<Weekday, number>()
  for (let w = 0; w < 7; w++) {
    const of = days.filter((d) => weekdayOf(d.date) === w)
    const t = totalsByCurrency(of, (d) => d.spentCents, () => currency).find((x) => x.currency === currency)
    per.set(w as Weekday, t?.cents ?? 0)
  }
  const top = Math.max(...per.values())
  if (top <= 0) return null
  const winners = [...per].filter(([, c]) => c === top)
  return winners.length === 1 ? (winners[0]?.[0] ?? null) : null
}
